import { type IVector3, Matrix34 } from '@seddi/math-v2';
import { FidelityStates, FidelityStateProgresses } from '@seddi/scene';
import {
  type Face,
  type Object3D,
  type Vector2,
  Matrix4,
  Mesh,
  PlaneGeometry,
  Quaternion,
  Vector3,
} from '@seddi/three';
import { type Pointer, CURSORS } from '@seddi/ui-viewport-core';
import { isHotkeyPressed } from 'react-hotkeys-hook';

import { Gizmo } from '../../../renderables/gizmo';
import Materials from '../../../renderer3/materials';
import { GIZMO_TYPES } from '../../../utils/constants';
import type { CameraToolArgs } from './cameraTool';
import { GizmoModes } from './gizmoTypes';
import { type SelectionToolUpdateArgs, SelectionTool } from './selectionTool';

export type TransformToolArgs = CameraToolArgs & { actions: Record<string, any> };

export type TransformToolUpdateArgs = SelectionToolUpdateArgs & { gizmoMode: GizmoModes };

type IDragging = {
  action?: string;
  axis?: string;
  offset?: IVector3;
  origin?: IVector3;
  started?: boolean;
};

// TODO: Move into Raycaster
type IHit = {
  distance: number;
  face?: Face;
  faceIndex?: number;
  instanceId?: number;
  object: Object3D;
  point: Vector3;
  uv?: Vector2;
};

export class TransformTool extends SelectionTool {
  currentGizmo: Gizmo;
  gizmoMode: GizmoModes;
  dragging: IDragging;
  geometryFreeze: boolean;
  gizmo: Gizmo;
  intersect: Mesh;
  transform: Matrix34;
  transformObjects: Object3D[];
  translation: Vector3;
  _quaternion: Quaternion;
  _vector0: Vector3;
  _vector1: Vector3;

  static rotationSpeed = 10;
  static worldOrigin = new Vector3(0, 0, 0);
  static worldX = new Vector3(1, 0, 0);
  static worldY = new Vector3(0, 1, 0);
  static worldZ = new Vector3(0, 0, 1);

  static filterTransformable(selectable) {
    return selectable.filter(
      ({ parent: { userData } }) => !!userData.avatar || !!userData.garmentPiece || !!userData.light,
    );
  }

  /**
   * Creates a new 3D transform tool
   * @param args Constructor arguments that contains information about camera, camera controls, pointer, viewport and actions
   */
  constructor(args: TransformToolArgs) {
    super(args);

    this.intersect = new Mesh(new PlaneGeometry(100000, 100000, 2, 2), Materials.Intersect);
    this.translation = new Vector3();
    this.transform = new Matrix34();
    this.gizmo = new Gizmo();
    this.currentGizmo = this.gizmo;
    this.gizmoMode = GizmoModes.NONE;
    this.entities.add(this.gizmo);

    this._quaternion = new Quaternion();
    this._vector0 = new Vector3();
    this._vector1 = new Vector3();
  }

  /**
   * Updates tool in terms of hover information, scene, selectables and selection objects with respect to the given arguments and gizmo transformation
   * @param args Update arguments that contains information about hover, renderables, scene and selection
   */
  onUpdate(args: TransformToolUpdateArgs) {
    const { selection: prevSelection, selectionObjects: prevSelectionObjects, isBlocked } = this;

    super.onUpdate(args);

    const { selection, selectionObjects, blockedActions } = this;

    this.isBlocked =
      Object.keys(blockedActions).length > 0 ? blockedActions['VIEWPORT_3D_TOOL_TRANSFORM'] : false;

    if (selectionObjects !== prevSelectionObjects || this.gizmoMode !== args.gizmoMode) {
      this.transformObjects = TransformTool.filterTransformable(selectionObjects);
      if (this.currentGizmo.type !== GIZMO_TYPES.ARRANGEMENT) {
        const isGizmoVisible = !!this.transformObjects?.length && args.gizmoMode == 'DEFAULT';
        this.currentGizmo.update(isGizmoVisible);
        this.gizmoMode = args.gizmoMode;
      }

      if (selection !== prevSelection && !this.isBlocked) {
        this.setTranslation();
        this.currentGizmo.position.copy(this.translation);
      }
    }
  }

  /**
   * Updates the cursor and conducts pointer actions with respect to the given arguments
   * @param args Object contains distance, pointer and cursor information
   * @returns Flag indicating the camera needs update
   */
  onAnimationTick({ delta, pointer }: TransformToolUpdateArgs): boolean {
    const { camera, currentGizmo, raycaster, transformObjects } = this;

    const cursor = { image: CURSORS.VIEWPORT_DEFAULT };
    let needsUpdate = false;
    if (!this.geometryFreeze) {
      if (!this.dragging && pointer.hover && !isHotkeyPressed('space') && transformObjects.length) {
        // Check intersection with handlers
        raycaster.setFromCamera(pointer.normalized, camera);
        const hit = raycaster.intersectObjects(currentGizmo.handlers)[0];

        needsUpdate = currentGizmo.setHover(hit ? (hit.object as any).action : false);

        if (pointer.primaryDown && currentGizmo.visible) {
          if (hit) {
            // Setup dragging
            this.setupDrag((hit.object as any).action);
          } else if (!isHotkeyPressed('shift')) {
            // Check intersection with transformObjects
            const hit = raycaster.intersectObjects(transformObjects)[0];
            if (hit) {
              currentGizmo.type === GIZMO_TYPES.DEFAULT && currentGizmo.position.copy(hit.point);
              return true;
            }
          }
        }
      }

      // Process dragging
      if (this.dragging) {
        const { dragging, actions, scene } = this;

        if (pointer.primaryDragDown) {
          // Start dragging
          dragging.started = true;
          const { fidelityState } = scene;
          fidelityState.state === FidelityStates.Drape &&
            fidelityState.progress === FidelityStateProgresses.Done &&
            actions.updateFidelityState(FidelityStates.ReDrape);
        }
        if (dragging.started) {
          this.onDrag({ pointer });
        }
        if (pointer.primaryUp || isHotkeyPressed('escape')) {
          // Stop dragging
          delete this.dragging;
          return true;
        }

        return dragging.started;
      }
    }

    // Selection/Orbit
    needsUpdate =
      super.onAnimationTick({
        cursor,
        delta,
        pointer,
      }) || needsUpdate;

    return needsUpdate;
  }

  /**
   * Gets the action corresponding to the given handler and updates dragging information if available
   * @param handler String indicating rotation or translation
   */
  setupDrag(handler: string) {
    const { camera, currentGizmo, intersect, raycaster, _vector0, _vector1 } = this;

    const action = this.getAction(handler);
    const axis = this.getAxis(handler, action);

    if (action === 'translation') {
      const { worldX, worldY, worldZ } = TransformTool;
      const eyeDir = ThreeMathTmp.Vector3[0];
      camera.getWorldPosition(eyeDir);
      eyeDir.sub(currentGizmo.position).normalize();
      _vector0.copy(worldY);

      switch (axis) {
        case 'X':
          _vector0.copy(eyeDir).cross(worldX);
          _vector1.copy(worldX).cross(_vector0);
          break;
        case 'Y':
          _vector0.copy(eyeDir).cross(worldY);
          _vector1.copy(worldY).cross(_vector0);
          break;
        case 'Z':
          _vector0.copy(eyeDir).cross(worldZ);
          _vector1.copy(worldZ).cross(_vector0);
          break;
        case 'XY':
          _vector1.copy(worldZ);
          break;
        case 'XZ':
          _vector0.copy(worldZ);
          _vector1.copy(worldY);
          break;
        case 'ZY':
          _vector1.copy(worldX);
          break;
        case 'XYZ':
          _vector1.set(0, 0, 0);
          break;
        default:
          break;
      }
    }

    this.setIntersect(action);

    const hit = raycaster.intersectObject(intersect)[0];
    if (hit) {
      this.setDragging(action, axis, hit);
    }
  }

  /**
   * Transforms the related objects if the pointer hits some
   * @param pointer Pointer input data
   */
  onDrag({ pointer }: { pointer: Pointer }) {
    const {
      camera,
      dragging: { action, axis, origin },
      intersect,
      raycaster,
    } = this;

    // Handle dragging
    raycaster.setFromCamera(pointer.normalized, camera);
    const hit = raycaster.intersectObject(intersect)[0];
    if (!hit) {
      return;
    }

    switch (action) {
      case 'rotation': {
        const unit = TransformTool[`world${axis}`];
        this.setRotationTransform(unit, hit);
        break;
      }
      case 'translation': {
        switch (axis) {
          case 'X':
            hit.point.y = origin.y;
            hit.point.z = origin.z;
            break;
          case 'Y':
            hit.point.x = origin.x;
            hit.point.z = origin.z;
            break;
          case 'Z':
            hit.point.x = origin.x;
            hit.point.y = origin.y;
            break;
          case 'XY':
            hit.point.z = origin.z;
            break;
          case 'XZ':
            hit.point.y = origin.y;
            break;
          case 'ZY':
            hit.point.x = origin.x;
            break;
          default:
            break;
        }
        this.setTranslationTransform(hit);
        break;
      }
      default:
        break;
    }

    // Dispatch action
    this.transformPieces(pointer);
  }

  /**
   * Transforms pieces with respect to given pointer and current transformation
   * @param pointer Pointer input data
   * @param _boneDisplacement Amount of translation for bones
   * @param _boneRotation Amount of rotation for bones
   */
  transformPieces(
    pointer: Pointer,
    _boneDisplacement: number = undefined,
    _boneRotation: number = undefined,
  ) {
    const { actions, transform, transformObjects } = this;
    actions.transformPieces3({
      ids: transformObjects.map((o) => o.parent.name),
      matrix: transform,
      isFirst: pointer.primaryDragDown,
      isDragging: !pointer.primaryUp,
    });
  }

  /**
   * Transforms the tool intersect with respect to the given action and updates its world matrix
   * @param action String indicating translation or rotation
   */
  setIntersect(action: string) {
    const { camera, intersect, _vector0, _vector1, currentGizmo } = this;
    if ((_vector1.length() === 0 && action === 'translation') || action === 'rotation') {
      camera.getWorldQuaternion(intersect.quaternion);
    } else {
      const m = ThreeMathTmp.Matrix4[0];
      m.lookAt(TransformTool.worldOrigin, _vector1, _vector0);
      intersect.quaternion.setFromRotationMatrix(m);
    }
    intersect.position.copy(currentGizmo.position);
    intersect.updateMatrixWorld();
  }

  /**
   * Sets the dragging information with respect to given action, axis and hit data
   * @param action String indicating translation or rotation
   * @param axis String indicating the axis
   * @param hit Hit data
   */
  setDragging(action: string, axis: string, hit: IHit) {
    this.dragging = {
      action,
      axis,
      offset: {
        x: hit.point.x - this.currentGizmo.position.x,
        y: hit.point.y - this.currentGizmo.position.y,
        z: hit.point.z - this.currentGizmo.position.z,
      },
      origin: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
    };
  }

  /**
   * Calculates the amount of rotation with respect to given unit vector and hit point
   * @param unit Vector to calculate the rotation around
   * @param hit Hit data
   * @returns Amount of rotation
   */
  getRotationAngle(unit: Vector3, hit: IHit): number {
    const {
      camera,
      dragging: { origin },
      currentGizmo,
      _vector0,
      _vector1,
    } = this;
    const position = currentGizmo.position;
    camera.getWorldPosition(_vector1);
    const speed = TransformTool.rotationSpeed / position.distanceTo(_vector1);
    _vector0.copy(unit);
    _vector1.sub(position).normalize();
    const hitVec = ThreeMathTmp.Vector3[0];
    hitVec.copy(hit.point).sub(origin);
    const rotationAngle = hitVec.dot(_vector0.cross(_vector1).normalize()) * speed;
    return rotationAngle;
  }

  /**
   * Transforms the tool with respect to the rotation angle to corresponding to the hit and gizmo position
   * @param unit Vector to calculate the rotation around
   * @param hit Hit data
   */
  setRotationTransform(unit: Vector3, hit: IHit) {
    const { currentGizmo, transform, _quaternion } = this;
    const position = currentGizmo.position;
    const rotationAngle = this.getRotationAngle(unit, hit);
    _quaternion.setFromAxisAngle(unit, rotationAngle);
    transform.makeTranslation(position);
    const m = MathTmp.Matrix34[0];
    transform.multiplyInPlace(m.makeRotationFromQuaternion(_quaternion));
    transform.multiplyInPlace(m.makeTranslationFromFloats(-position.x, -position.y, -position.z));
  }

  /**
   * Translates the tool with respect to the selection objects
   */
  setTranslation() {
    const { selectionObjects, translation } = this;

    const vec0 = ThreeMathTmp.Vector3[0];

    translation.set(0, 0, 0);

    selectionObjects.forEach((entity: any) => {
      if (!entity.geometry.boundingSphere) {
        entity.geometry.computeBoundingSphere();
      }

      entity.updateMatrix();
      vec0.copy(entity.geometry.boundingSphere.center);
      vec0.applyMatrix4(entity.matrix);
      translation.add(vec0);
    });

    translation.multiplyScalar(1 / selectionObjects.length);
  }

  /**
   * Transforms the tool with respect to the given hit data and current dragging information
   * @param hit Hit data
   */
  setTranslationTransform(hit: IHit) {
    const {
      dragging: { offset, origin },
      currentGizmo,
      intersect,
      transform,
    } = this;
    const position = currentGizmo.position;
    position.copy(hit.point).sub(offset);
    intersect.position.copy(position);
    intersect.updateMatrixWorld();
    transform.makeTranslationFromFloats(
      position.x + offset.x - origin.x,
      position.y + offset.y - origin.y,
      position.z + offset.z - origin.z,
    );
  }

  /**
   * Returns the action corresponding to the given handler
   * @param handler String indicating rotation or translation
   * @returns String indicating the action as rotation or translation
   */
  getAction(handler: string): string {
    return handler.indexOf('rotation') === 0 ? 'rotation' : 'translation';
  }

  /**
   * Returns the axis corresponding to the given handler and action
   * @param handler String indicating rotation or translation
   * @param action String indicating rotation or translation
   * @returns String indicating the axis as rotation or translation
   */
  getAxis(handler: string, action: string): string {
    return action === 'rotation' ? handler.substr(8) : handler.substr(11);
  }
}

class MathTmp {
  static Matrix34 = [new Matrix34()];
}

class ThreeMathTmp {
  static Vector3 = [new Vector3(), new Vector3(), new Vector3(), new Vector3()];
  static Matrix4 = [new Matrix4()];
}
